import { Component, Input, OnInit, OnDestroy, ViewEncapsulation, Injector, Inject, PLATFORM_ID } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Device } from "../../model/Device";
import { AbstractComponent } from '../abstract.component';
import { EvaSize } from '@shopping-angular-components/loader';
import { Module } from 'projects/common/model/ModuleModel';
import { isPlatformBrowser } from '@angular/common';

interface DocumentData {
  link: string;
}

interface PdfOptions {
  fitToPage: boolean;
  zoom: number;
}

@Component({
  selector: 'document-viewer',
  template: `
      <div [ngClass]="{'buttons-bar -eva-3-bc-brand-primary-4 -eva-3-valign -eva-3-pl-md': true, '-align-right': !webview}">
          <eva-button *ngIf="this.device.type === 'DESKTOP'" class="-eva-3-mr-md"
                      [options]="{ghost_button: false, type: '-dark', size: '-sm', link_text: 'common.download' | translate}"
                      (clickEvent)="downloadDocument()"
                      [moduleId]="getModuleId()"
                      [moduleIndex]="module.row_number"
                      [uniqueId]="module.unique_id"></eva-button>

          <eva-button *ngIf="this.device.type === 'DESKTOP' && !webview" class="-eva-3-mr-md"
                      [options]="{ghost_button: false, type: '-dark', size: '-sm', link_text: 'common.print' | translate}"
                      (clickEvent)="printDocument()"
                      [moduleId]="getModuleId()"
                      [moduleIndex]="module.row_number"
                      [uniqueId]="module.unique_id"></eva-button>
      </div>
      <div id="pdf-document" class="pdf-container">
          <div *ngIf="loading" class="loader-container -eva-3-mt-md">
            <loader product="nymeria-gui" flow="landing" [show]="true" [size]="evaSize.LARGE"></loader>
          </div>
          <iframe *ngIf="!loading && safeBlobUrl && useGoogleViewer"
                  [src]="safeBlobUrl"
                  class="pdf-frame"
                  frameborder="0"
                  allowfullscreen></iframe>
          <embed *ngIf="!loading && safeBlobUrl && !useGoogleViewer"
                 [src]="safeBlobUrl"
                 type="application/pdf"
                 class="pdf-frame"/>
      </div>
  `,
  styleUrls: ['./document-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentViewerComponent extends AbstractComponent implements OnInit, OnDestroy {
  private static MODULE_ID: string = "DocumentModule";

  @Input() override module: Module<DocumentData>;
  @Input() device: Device;
  source: string;

  option: PdfOptions = { fitToPage: true, zoom: 1 };
  loading: boolean = true;
  useGoogleViewer: boolean = false;
  
  private rawBlobUrl: string = '';
  safeBlobUrl: SafeResourceUrl | null = null;
  private pdfBlob: Blob | null = null;
  evaSize = EvaSize;
  private readonly DOCUMENT_URL = '//media.staticontent.com/media/documents/';
  private readonly BACKEND_DOCUMENT_URL = '/nymeria-gui/data/model/documents/';
  private sanitizer: DomSanitizer;
  webview: boolean;

  constructor(injector: Injector, sanitizer: DomSanitizer, @Inject(PLATFORM_ID) private platformId: Object) {
    super(injector);
    const landingModel = this.landingModelService.getLandingModel();
    this.webview = landingModel.webview;
    this.sanitizer = sanitizer;
  }
  
  ngOnInit(): void {
    this.source = this.module.data.link;
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      if (this.isMobileAndroid()) {
        this.useGoogleViewer = true;
        this.safeBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.getGoogleViewerUrl());
        this.loadedHandler();
      } else if (this.isMobileOrApp()) {
        this.loadPdf(this.getBackendDocumentUrl());
      } else {
        this.loadPdf(this.getPdfUrl());
      }
    }
  }

  async printDocument(): Promise<void> {
    const sourceUrl = this.getPdfUrl();
    try {
      const blob = await this.ensureBlobForPrint(sourceUrl);
      if (!blob) {
        this.openFallback(sourceUrl);
        return;
      }
      await this.printBlob(blob);
    } catch (error) {
      this.openFallback(sourceUrl);
    }
  }

  private async ensureBlobForPrint(sourceUrl: string): Promise<Blob | null> {
    if (this.pdfBlob) return this.pdfBlob;
    try {
      // source is already a blob: URL, try to fetch it (same-origin) and store
      if (sourceUrl && sourceUrl.startsWith('blob:')) {
        const resp = await fetch(sourceUrl);
        if (!resp.ok) return null;
        const blob = await resp.blob();
        this.pdfBlob = blob;
        return blob;
      }
      // try to fetch and store
      return await this.fetchAndStoreBlob(sourceUrl);
    } catch (e) {
      return null;
    }
  }

  private printBlob(blob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const sourceUrl = URL.createObjectURL(blob);
      // we are printing a blob: URL (same-origin for object URLs)
      if (!sourceUrl.startsWith('blob:')) {
        reject(new Error('printBlob requires a blob URL'));
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.title = 'pdf Iframe';
      iframe.className = 'pdfIframe';
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const cleanup = () => {
        try { URL.revokeObjectURL(sourceUrl); } catch (e) { }
        try { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); } catch (e) { }
      };

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.focus();
            iframe.contentWindow?.print();
            resolve();
          } catch (e) {
            reject(e);
          } finally {
            cleanup();
          }
        }, 1);
      };

      iframe.src = sourceUrl;
    });
  }

  private openFallback(url: string): void {
    window.open(url, '_blank', 'noopener');
  }

  async downloadDocument(): Promise<void> {
    const sourceUrl = this.getPdfUrl();

    if (this.webview) {
      this.openFallback(sourceUrl);
      return;
    }

    try {
      if (!this.pdfBlob) {
        await this.fetchAndStoreBlob(sourceUrl);
      }

      if (this.pdfBlob) {
        const objUrl = URL.createObjectURL(this.pdfBlob);

        const a = document.createElement('a');
        a.href = objUrl;
        a.download = `documento-${Date.now()}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => {
          try { URL.revokeObjectURL(objUrl); } catch (e) {}
        }, 1000);
        return;
      }
    } catch (error) {
      console.warn('Falling back to open URL', error);
      this.openFallback(sourceUrl);
    }
  }

  async loadPdf(link: string) {
    try {
      const blob = await this.fetchAndStoreBlob(link);
      if (this.rawBlobUrl) {
        try { URL.revokeObjectURL(this.rawBlobUrl); } catch (e) {}
        this.rawBlobUrl = '';
        this.safeBlobUrl = null;
      }
      this.rawBlobUrl = URL.createObjectURL(blob);
      const urlWithOptions = `${this.rawBlobUrl}#${this.buildPdfOptions()}`;
      this.safeBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlWithOptions);
      this.loadedHandler();
    } catch (error) {
      console.error('Loading error:', error);
      this.loadedHandler();
    }
  }

  private async fetchAndStoreBlob(link: string): Promise<Blob> {
    if (this.pdfBlob) return this.pdfBlob;
    this.loading = true;
    try {
      const resp = await fetch(link);
      if (!resp.ok) throw new Error('Download error');
      const blob = await resp.blob();
      this.pdfBlob = blob;
      return blob;
    } finally {
      this.loading = false;
    }
  }
  
  private buildPdfOptions(): string {
    let urlOptions = 'toolbar=0';
    if (this.option.fitToPage) {
      urlOptions += '&view=FitH';
    }
    return urlOptions;
  }

  ngOnDestroy(): void {
    if (this.rawBlobUrl) {
      try { URL.revokeObjectURL(this.rawBlobUrl); } catch (e) {}
      this.rawBlobUrl = '';
    }

    this.safeBlobUrl = null;
    this.pdfBlob = null;
  }

  loadedHandler() {
    this.loading = false;
  }

  getModuleId(): string {
    return DocumentViewerComponent.MODULE_ID;
  }

  private getPdfUrl(): string {
    // prefer the in-memory blob URL if present
    if (this.rawBlobUrl) return this.rawBlobUrl;
    const base = this.getMediaDocumentUrl();
    if (base.startsWith('//')) {
      return window.location.protocol + base;
    }
    return base;
  }

  private isMobileAndroid(): boolean {
    return String(this.device?.platform) === 'ANDROID_APP' || String(this.device?.so) === 'ANDROID';
  }

  private isMobileOrApp(): boolean {
    return this.webview || ['MOBILE', 'IPHONE_APP', 'ANDROID_APP'].includes(this.device?.type);
  }

  private getGoogleViewerUrl(): string {
    const documentUrl = this.getMediaDocumentUrl();
    const fullUrl = documentUrl.startsWith('//') ? `https:${documentUrl}` : documentUrl;
    return `https://docs.google.com/viewer?url=${encodeURIComponent(fullUrl)}&embedded=true`;
  }

  private getMediaKey(): string {
    if (this.source && this.source.includes(this.DOCUMENT_URL)) {
      return this.source.split(this.DOCUMENT_URL).pop() || this.source;
    }
    return this.source;
  }

  private getBackendDocumentUrl(): string {
    return this.BACKEND_DOCUMENT_URL + this.getMediaKey();
  }

  private getMediaDocumentUrl() {
    if (this.source && this.source.includes(this.DOCUMENT_URL)) {
      return this.source;
    }
    return this.DOCUMENT_URL + this.source;
  }
}
